/**
 * NxMarcasPropuestas — la pestana "Por autorizar" de Asistencia (ARG-MARCAJE-PROPONE-T1, nexo-api 329).
 *
 * Audiencia: el jefe o el administrador del negocio, en PC o telefono. Por persona, las marcas que Argemiro propone
 * para completar dias incompletos: fecha, que marca falta, la hora sugerida (editable) y por que. Un boton por marca;
 * nada se escribe sin ese boton. La logica (adaptar, cuerpo, mensajes) vive en application/asistencia/marcasPropuestas.js.
 *
 * @module presentation/shared/NxMarcasPropuestas v1.0
 */
import { AsistenciaApiClient } from '../../infrastructure/api/AsistenciaApiClient.js';
import { AppConfig } from '../../config.js';
import { adaptarPropuestas, cuerpoAutorizar, mensajeAutorizar, TIPO_MARCA } from '../../application/asistencia/marcasPropuestas.js';

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc } from './texto.js';
const fechaLarga = (f) => {
  const d = new Date(f + 'T12:00:00');
  return Number.isNaN(d.getTime()) ? f : d.toLocaleDateString('es-CO', { weekday: 'short', day: 'numeric', month: 'short' });
};

// Estilos acotados a .nx-mp, una sola vez (no tocan el resto del panel). Filas de 40-44px, numeros a la derecha.
function estilos() {
  if (document.getElementById('nx-mp-estilos')) return;
  const s = document.createElement('style'); s.id = 'nx-mp-estilos';
  s.textContent = `.nx-mp{display:flex;flex-direction:column;gap:12px}
.nx-mp-cabeza{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;flex-wrap:wrap}
.nx-mp-ayuda{font-size:0.875rem;opacity:.75;margin-top:4px}.nx-mp-rango{display:flex;gap:8px}
.nx-mp-total{font-size:0.875rem;opacity:.8}.nx-mp-persona h4{margin:8px 0 6px}.nx-mp-cuenta{font-size:0.875rem;opacity:.7;margin-left:6px}
.nx-mp-fila{display:grid;grid-template-columns:110px minmax(120px,1fr) auto 96px minmax(0,2fr) auto;gap:8px;align-items:center;min-height:44px;padding:4px 8px;border-radius:8px;background:rgba(255,255,255,.03)}
.nx-mp-fila .nx-mp-eco{grid-column:1/-1;font-size:0.875rem;color:#e7c07a}.nx-mp-fila .nx-mp-eco:empty{display:none}.nx-mp-eco-ok{color:#8fd694!important}
.nx-mp-hora{min-width:0!important;width:96px}.nx-mp-hora-l{font-size:0.875rem;opacity:.7}.nx-mp-motivo{font-size:0.875rem;opacity:.8;overflow:hidden;text-overflow:ellipsis}
.nx-mp-hecha{opacity:.6}.nx-mp-esqueleto{height:44px;background:rgba(255,255,255,.06)}
@media (max-width:720px){.nx-mp-fila{grid-template-columns:1fr 1fr}.nx-mp-motivo{grid-column:1/-1}}`;
  document.head.append(s);
}

/** Pinta la pestana en `el` para el negocio. `rango`: undefined (7 dias hasta ayer) o {desde, hasta}. */
export async function renderMarcasPropuestas(el, businessId, rango) {
  if (!el) return;
  estilos();
  // Esqueleto que imita la lista (regla 12: nunca pantalla en blanco).
  el.innerHTML = `<div class="nx-mp" data-nx-marcas-propuestas>
    <div class="nx-mp-cabeza"><strong>Marcas que Argemiro propone</strong></div>
    ${[1, 2, 3].map(() => '<div class="nx-mp-fila nx-mp-esqueleto"></div>').join('')}
  </div>`;
  const res = await AsistenciaApiClient.marcasPropuestas(businessId, rango || {});
  const v = adaptarPropuestas(res, { env: AppConfig.env });
  const raiz = el.querySelector('[data-nx-marcas-propuestas]');
  if (!raiz) return;
  const cabeza = `<div class="nx-mp-cabeza">
      <div><strong>Marcas que Argemiro propone</strong>
        <div class="nx-mp-ayuda">Días incompletos: la marca que falta, con la hora sugerida. Nada se escribe hasta que la autorices.</div></div>
      <div class="nx-mp-rango">
        <button class="btn btn-ghost btn-sm" data-mp-rango="semana">Últimos 7 días</button>
        <button class="btn btn-ghost btn-sm" data-mp-rango="mes">Este mes</button>
      </div>
    </div>`;
  if (v.estado !== 'ok') {
    raiz.innerHTML = cabeza + `<div class="empty-state nx-mp-vacio"><p>${esc(v.causa)}</p>
      <button class="btn btn-ghost btn-sm" data-mp-recargar>Actualizar</button></div>`;
  } else {
    raiz.innerHTML = cabeza + `<div class="nx-mp-total">${v.total} marca${v.total === 1 ? '' : 's'} por autorizar · ${esc(v.desde)} a ${esc(v.hasta)}</div>` +
      v.personas.map((per) => `<section class="nx-mp-persona">
        <h4>${esc(per.nombre)} <span class="nx-mp-cuenta">${per.propuestas.length}</span></h4>
        ${per.propuestas.map((p, i) => `<div class="nx-mp-fila" data-mp-fila="${esc(per.colaboradorId)}|${esc(p.fecha)}|${esc(p.tipo)}">
          <span class="nx-mp-fecha">${esc(fechaLarga(p.fecha))}</span>
          <span class="nx-mp-tipo">${esc(TIPO_MARCA[p.tipo] || p.tipo)}</span>
          <label class="nx-mp-hora-l" for="mp-hora-${esc(per.colaboradorId)}-${i}">Hora</label>
          <input type="time" id="mp-hora-${esc(per.colaboradorId)}-${i}" class="form-control nx-mp-hora" value="${esc(p.horaSugerida)}" data-mp-hora>
          <span class="nx-mp-motivo" title="${esc(p.fuente)}">${esc(p.motivo || p.fuente)}</span>
          <button class="btn btn-primary btn-sm" data-mp-autorizar>Autorizar</button>
          <span class="nx-mp-eco" role="status"></span>
        </div>`).join('')}
      </section>`).join('');
  }
  // Rango
  raiz.querySelectorAll('[data-mp-rango]').forEach((b) => b.addEventListener('click', () => {
    if (b.dataset.mpRango === 'mes') {
      const hoy = new Date(), d = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
      const iso = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
      renderMarcasPropuestas(el, businessId, { desde: iso(d), hasta: iso(hoy) });
    } else renderMarcasPropuestas(el, businessId);
  }));
  raiz.querySelector('[data-mp-recargar]')?.addEventListener('click', () => renderMarcasPropuestas(el, businessId, rango));
  // Autorizar: una marca por boton; el boton se deshabilita mientras pregunta (regla 11).
  if (v.estado !== 'ok') return;
  const porClave = new Map();
  for (const per of v.personas) for (const p of per.propuestas) porClave.set(`${per.colaboradorId}|${p.fecha}|${p.tipo}`, p);
  raiz.querySelectorAll('[data-mp-fila]').forEach((fila) => {
    const p = porClave.get(fila.dataset.mpFila);
    const boton = fila.querySelector('[data-mp-autorizar]'), eco = fila.querySelector('.nx-mp-eco'), hora = fila.querySelector('[data-mp-hora]');
    boton.addEventListener('click', async () => {
      boton.disabled = true; eco.textContent = 'Autorizando…';
      const r = await AsistenciaApiClient.autorizarMarcaPropuesta(businessId, cuerpoAutorizar(p, hora.value));
      const m = mensajeAutorizar(r);
      eco.textContent = m.texto;
      eco.classList.toggle('nx-mp-eco-ok', m.ok);
      if (m.ok) { hora.disabled = true; boton.textContent = 'Autorizada'; fila.classList.add('nx-mp-hecha'); }
      else boton.disabled = false;
    });
  });
}
